/**
 * Poll Frontend Handler
 * 
 * @since 3.9.0
 */

(function($) {
    'use strict';

    // Custom Modal Function
    function showCustomModal(title, message, duration = 3000, type = 'success') {
        const modalOverlay = document.createElement('div');
        modalOverlay.className = 'custom-modal-overlay';

        const modalContent = document.createElement('div');
        modalContent.className = `custom-modal ${type}`;

        const modalHeader = document.createElement('div');
        modalHeader.className = 'custom-modal-header';

        const modalTitle = document.createElement('h3');
        modalTitle.textContent = title;
        modalHeader.appendChild(modalTitle);

        const modalBody = document.createElement('div');
        modalBody.className = 'custom-modal-body';
        modalBody.innerHTML = message;

        const progressBar = document.createElement('div');
        progressBar.className = 'custom-modal-progress';

        const progressIndicator = document.createElement('div');
        progressIndicator.className = 'custom-modal-progress-indicator';
        progressBar.appendChild(progressIndicator);

        modalContent.appendChild(modalHeader);
        modalContent.appendChild(modalBody);
        modalContent.appendChild(progressBar);
        modalOverlay.appendChild(modalContent);

        document.body.appendChild(modalOverlay);

        setTimeout(() => {
            modalOverlay.classList.add('visible');
            progressIndicator.style.transition = `width ${duration}ms linear`;
            progressIndicator.style.width = '100%';
        }, 10);

        setTimeout(() => {
            modalOverlay.classList.remove('visible');
            setTimeout(() => {
                document.body.removeChild(modalOverlay);
            }, 300);
        }, duration);

        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                modalOverlay.classList.remove('visible');
                setTimeout(() => {
                    document.body.removeChild(modalOverlay);
                }, 300);
            }
        });
    }

    class PollFrontend {
        constructor(container) {
            this.container = $(container);
            this.pollId = this.container.data('poll-id');
            this.poll = null;
            this.hasVoted = false;
            this.userData = {};
            this.hasLocalVote = false;
            this.localVoteData = null;
            this.currentStep = 0;
            this.totalSteps = 0;
            this.selectedAnswers = [];
            this.userFormData = {}; // Store user form data across steps
            
            this.init();
        }

        init() {
            // Check localStorage for vote status (always check for hybrid and browser_storage methods)
            this.checkLocalVoteStatus();
            this.loadPoll();
        }

        checkLocalVoteStatus() {
            const storageKey = `simpleform_poll_voted_${this.pollId}`;
            const votedData = localStorage.getItem(storageKey);
            
            if (votedData) {
                try {
                    const data = JSON.parse(votedData);
                    this.localVoteData = data;
                    this.hasLocalVote = true;
                } catch (e) {
                    // Invalid JSON, remove it
                    localStorage.removeItem(storageKey);
                    this.hasLocalVote = false;
                    this.localVoteData = null;
                }
            } else {
                this.hasLocalVote = false;
                this.localVoteData = null;
            }
        }

        setLocalVoteStatus(voteData) {
            const storageKey = `simpleform_poll_voted_${this.pollId}`;
            localStorage.setItem(storageKey, JSON.stringify({
                timestamp: Date.now(),
                pollId: this.pollId,
                ...voteData
            }));
            this.hasLocalVote = true;
        }

        clearLocalVoteStatus() {
            const storageKey = `simpleform_poll_voted_${this.pollId}`;
            localStorage.removeItem(storageKey);
            this.hasLocalVote = false;
            this.localVoteData = null;
        }

        loadPoll() {
            const self = this;
            
            $.ajax({
                url: simpleform_ajax.ajax_url,
                type: 'POST',
                data: {
                    action: 'simpleform_get_poll_for_frontend',
                    poll_id: self.pollId,
                    nonce: simpleform_ajax.nonce
                },
                success: function(response) {
                    if (response.success && response.data.poll) {
                        self.poll = response.data.poll;
                        self.hasVoted = response.data.has_voted;
                        
                        // Check if login is required and user is not logged in
                        if (self.poll.settings.require_login && !simpleform_ajax.is_user_logged_in) {
                            self.showLoginRequired();
                            return;
                        }
                        
                        // Check browser storage for vote tracking
                        if (self.checkBrowserTracking()) {
                            return;
                        }
                        
                        // Determine what to show based on vote status and settings
                        if (self.hasVoted || self.hasLocalVote) {
                            // User has already voted (server-side or client-side check)
                            if (self.poll.settings.allow_revote) {
                                // Allow revote - show poll form
                                self.renderPoll();
                                // Also show results if enabled
                                if (response.data.results && self.poll.settings.show_results !== 'never') {
                                    self.renderResults(response.data.results);
                                }
                            } else {
                                // Don't allow revote
                                if (self.poll.settings.hide_after_submit) {
                                    // Hide poll form, show results or thank you
                                    if (response.data.results && self.poll.settings.show_results !== 'never') {
                                        self.renderResults(response.data.results);
                                    } else {
                                        self.showCompletedMessage();
                                    }
                                } else {
                                    // Don't hide poll form, but show thank you with results (no disabled form)
                                    self.showThankYouWithResults(response.data.results);
                                }
                            }
                        } else {
                            // Check if user has voted based on vote tracking method
                            const hasVotedByTracking = self.checkVoteByTrackingMethod();
                            
                            if (hasVotedByTracking && !self.poll.settings.allow_revote) {
                                // User has voted according to tracking method
                                if (self.poll.settings.hide_after_submit) {
                                    if (response.data.results && self.poll.settings.show_results !== 'never') {
                                        self.renderResults(response.data.results);
                                    } else {
                                        self.showCompletedMessage();
                                    }
                                } else {
                                    self.showThankYouWithResults(response.data.results);
                                }
                            } else {
                                // User hasn't voted yet - show poll form
                                self.renderPoll();
                            }
                        }
                    } else {
                        self.showError(response.data.message || 'Failed to load poll');
                    }
                },
                error: function() {
                    self.showError('Failed to load poll. Please try again.');
                }
            });
        }

        checkBrowserTracking() {
            const voteTracking = this.poll.settings.vote_tracking || 'hybrid';
            
            // Only check localStorage for browser_storage and hybrid methods
            if (voteTracking === 'browser_storage' || voteTracking === 'hybrid') {
                if (this.hasLocalVote && (!this.poll.settings.allow_revote)) {
                    this.showAlreadyVoted();
                    return true;
                }
            }
            
            return false;
        }

        showAlreadyVoted() {
            const html = `
                <div class="poll-wrapper">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    <div class="poll-already-voted">
                        <div class="already-voted-content">
                            <h3>🗳️ Vote Already Submitted</h3>
                            <p>You have already voted in this poll. ${this.poll.settings.allow_revote ? 'You can vote again if you wish.' : 'Multiple votes are not allowed.'}</p>
                            ${this.poll.settings.allow_revote ? '<button class="poll-revote-btn" type="button">Vote Again</button>' : ''}
                        </div>
                    </div>
                </div>
            `;
            
            this.container.html(html);
            
            // Attach revote event if allowed
            if (this.poll.settings.allow_revote) {
                const self = this;
                this.container.find('.poll-revote-btn').on('click', function() {
                    self.renderPoll();
                });
            }
        }

        checkVoteByTrackingMethod() {
            const voteTracking = this.poll.settings.vote_tracking || 'hybrid';
            
            switch (voteTracking) {
                case 'ip_address':
                    // Server-side IP check only (already handled by hasVoted)
                    return this.hasVoted;
                    
                case 'browser_storage':
                    // Client-side localStorage check only
                    return this.hasLocalVote;
                    
                case 'hybrid':
                default:
                    // Both server-side IP and client-side localStorage
                    return this.hasVoted || this.hasLocalVote;
            }
        }

        shouldShowPoll() {
            // If user hasn't voted, always show poll
            if (!this.hasVoted && !this.hasLocalVote) {
                return true;
            }
            
            // If user has voted or has local vote
            if (this.hasVoted || this.hasLocalVote) {
                // If allow_revote is enabled, always show poll so they can change vote
                if (this.poll.settings.allow_revote) {
                    return true;
                }
                // If allow_revote is disabled, don't show poll form - show thank you card instead
                return false;
            }
            
            return true;
        }

        showCompletedMessage() {
            const message = this.poll.settings.allow_revote 
                ? 'You have already voted in this poll. You can change your vote by selecting new answers.'
                : 'Thank you for participating in this poll!';
            
            const submessage = this.poll.settings.allow_revote 
                ? '' 
                : 'Your vote has been recorded successfully.';
            
            this.container.html(`
                <div class="poll-completed">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    <div class="poll-message">
                        <div class="thank-you-content">
                            <div class="thank-you-icon">🎉</div>
                            <h3>Thank You!</h3>
                            <p>${message}</p>
                            ${submessage ? `<p class="sub-message">${submessage}</p>` : ''}
                        </div>
                    </div>
                </div>
            `);
        }

        showThankYouWithResults(results) {
            const html = `
                <div class="poll-completed">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    <div class="poll-message">
                        <div class="thank-you-content">
                            <div class="thank-you-icon">🎉</div>
                            <h3>Thank you for voting!</h3>
                            <p>Your vote has been recorded successfully.</p>
                        </div>
                    </div>
                    ${results && this.poll.settings.show_results !== 'never' ? `
                        <div class="poll-results-section">
                            <div class="poll-results-content">
                                <h3>Poll Results</h3>
                                <p class="total-votes">Total Votes: ${results.total_votes}</p>
                                <div class="results-list">
                                    ${results.answers.map(answer => `
                                        <div class="result-item">
                                            <div class="result-header">
                                                <span class="result-text">${this.escapeHtml(answer.text)}</span>
                                                <span class="result-stats">
                                                    ${this.poll.settings.result_display === 'percentage' || this.poll.settings.result_display === 'both' ? 
                                                        `<span class="percentage">${answer.percentage}%</span>` : ''}
                                                    ${this.poll.settings.result_display === 'count' || this.poll.settings.result_display === 'both' ? 
                                                        `<span class="count">${answer.votes} votes</span>` : ''}
                                                </span>
                                            </div>
                                            <div class="result-bar">
                                                <div class="result-bar-fill" style="width: ${answer.percentage}%"></div>
                                            </div>
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        </div>
                    ` : ''}
                </div>
            `;
            
            this.container.html(html);
        }

        renderPollWithThankYou(results) {
            // First render the poll form
            this.renderPoll();
            
            // Disable the poll form since user already voted
            this.container.find('input[name="poll_answer"]').prop('disabled', true);
            this.container.find('.poll-answer-option').addClass('disabled');
            this.container.find('.poll-submit-btn').prop('disabled', true).text('Already Voted');
            this.container.find('.poll-user-info input').prop('disabled', true);
            
            // Add thank you message at the top
            const thankYouHtml = `
                <div class="poll-thank-you-banner">
                    <div class="thank-you-content">
                        <span class="thank-you-icon">🎉</span>
                        <div class="thank-you-text">
                            <strong>Thank you for voting!</strong>
                            <p>Your vote has been recorded successfully.</p>
                        </div>
                    </div>
                </div>
            `;
            
            this.container.find('.poll-wrapper').prepend(thankYouHtml);
            
            // Show results if enabled
            if (results && this.poll.settings.show_results !== 'never') {
                this.renderResults(results);
            }
        }

        renderPoll() {
            // Check if login is required
            if (this.poll.settings.require_login && !simpleform_ajax.is_user_logged_in) {
                this.showLoginRequired();
                return;
            }
            
            const pollType = this.poll.settings.poll_type || 'single_question';
            const displayMode = this.poll.settings.display_mode || 'all_at_once';
            
            if (pollType === 'multiple_questions') {
                if (displayMode === 'step_by_step') {
                    this.renderMultipleQuestionsStepByStep();
                } else {
                    this.renderMultipleQuestionsAllAtOnce();
                }
            } else {
                // Single question poll
                if (displayMode === 'step_by_step') {
                    this.renderStepByStepPoll();
                } else {
                    this.renderAllAtOncePoll();
                }
            }
        }

        showLoginRequired() {
            const html = `
                <div class="poll-wrapper">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    <div class="poll-login-required">
                        <div class="login-required-content">
                            <h3>🔒 Login Required</h3>
                            <p>You must be logged in to vote in this poll.</p>
                            <a href="${simpleform_ajax.login_url || '/wp-login.php'}" class="poll-login-btn">Login</a>
                        </div>
                    </div>
                </div>
            `;
            
            this.container.html(html);
        }

        renderMultipleQuestionsAllAtOnce() {
            const needsUserInfo = this.poll.settings.collect_name || this.poll.settings.collect_email;
            const questions = this.poll.questions || [];
            
            const html = `
                <div class="poll-wrapper multiple-questions">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    
                    ${needsUserInfo ? this.renderUserInfoFields() : ''}
                    
                    <div class="poll-content">
                        <div class="survey-questions">
                            ${questions.map((question, questionIndex) => `
                                <div class="survey-question" data-question-id="${question.id}">
                                    <div class="question-header">
                                        <h3 class="question-title">
                                            <span class="question-number">${questionIndex + 1}.</span>
                                            ${this.escapeHtml(question.text)}
                                            ${question.required ? '<span class="required">*</span>' : ''}
                                        </h3>
                                        ${question.description ? `<p class="question-description">${this.escapeHtml(question.description)}</p>` : ''}
                                    </div>
                                    <div class="question-answers">
                                        ${question.answers.map((answer) => `
                                            <label class="poll-answer-option">
                                                <input type="${question.allow_multiple ? 'checkbox' : 'radio'}" 
                                                       name="question_${question.id}" 
                                                       value="${answer.id}"
                                                       ${question.required ? 'required' : ''}>
                                                <span class="answer-content">
                                                    ${answer.image_url ? `<img src="${answer.image_url}" alt="Answer image" class="answer-image">` : ''}
                                                    <span class="answer-text">${this.escapeHtml(answer.text)}</span>
                                                </span>
                                            </label>
                                        `).join('')}
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                    <div class="poll-footer">
                        <button class="poll-submit-btn" type="button">Submit Survey</button>
                    </div>
                    <div class="poll-results-container" style="display: none;"></div>
                </div>
            `;
            
            this.container.html(html);
            this.attachEvents();
        }

        renderMultipleQuestionsStepByStep() {
            const questions = this.poll.questions || [];
            this.totalSteps = questions.length;
            
            // Initialize current step if not set
            if (this.currentStep >= this.totalSteps) {
                this.currentStep = 0;
            }
            
            const currentQuestion = questions[this.currentStep];
            if (!currentQuestion) return;
            
            const needsUserInfo = this.poll.settings.collect_name || this.poll.settings.collect_email;
            
            const html = `
                <div class="poll-wrapper step-by-step multiple-questions">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    
                    ${this.renderStepNavigation()}
                    
                    ${needsUserInfo && this.currentStep === 0 ? this.renderUserInfoFields() : ''}
                    
                    <div class="poll-content">
                        <div class="poll-step-content">
                            <div class="step-header">
                                <h3>${this.getMultipleQuestionsStepTitle()}</h3>
                                <p class="step-description">
                                    ${currentQuestion.description || `Question ${this.currentStep + 1} of ${this.totalSteps}`}
                                    ${currentQuestion.required ? ' (Required)' : ' (Optional)'}
                                </p>
                            </div>
                            <div class="survey-question" data-question-id="${currentQuestion.id}">
                                <div class="question-answers">
                                    ${currentQuestion.answers.map((answer) => `
                                        <label class="poll-answer-option">
                                            <input type="${currentQuestion.allow_multiple ? 'checkbox' : 'radio'}" 
                                                   name="question_${currentQuestion.id}" 
                                                   value="${answer.id}"
                                                   ${this.selectedAnswers.includes(answer.id) ? 'checked' : ''}
                                                   ${currentQuestion.required ? 'required' : ''}>
                                            <span class="answer-content">
                                                ${answer.image_url ? `<img src="${answer.image_url}" alt="Answer image" class="answer-image">` : ''}
                                                <span class="answer-text">${this.escapeHtml(answer.text)}</span>
                                            </span>
                                        </label>
                                    `).join('')}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="poll-footer step-footer">
                        <div class="step-buttons">
                            ${this.currentStep > 0 ? '<button class="poll-prev-btn" type="button">Previous</button>' : ''}
                            ${this.currentStep < this.totalSteps - 1 ? 
                                '<button class="poll-next-btn" type="button">Next</button>' : 
                                '<button class="poll-submit-btn" type="button">Submit Survey</button>'
                            }
                        </div>
                    </div>
                    
                    <div class="poll-results-container" style="display: none;"></div>
                </div>
            `;
            
            this.container.html(html);
            this.attachStepEvents();
            
            // Restore user form data after rendering
            this.restoreUserFormData();
        }

        getMultipleQuestionsStepTitle() {
            const questions = this.poll.questions || [];
            const currentQuestion = questions[this.currentStep];
            return currentQuestion ? currentQuestion.text : `Question ${this.currentStep + 1}`;
        }

        renderAllAtOncePoll() {
            const inputType = this.poll.settings.allow_multiple ? 'checkbox' : 'radio';
            const needsUserInfo = this.poll.settings.collect_name || this.poll.settings.collect_email;
            
            const html = `
                <div class="poll-wrapper">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    
                    ${needsUserInfo ? this.renderUserInfoFields() : ''}
                    
                    <div class="poll-content">
                        <div class="poll-answers">
                            ${this.poll.answers.map((answer) => `
                                <label class="poll-answer-option">
                                    <input type="${inputType}" name="poll_answer" value="${answer.id}">
                                    <span class="answer-content">
                                        ${answer.image_url ? `<img src="${answer.image_url}" alt="Answer image" class="answer-image">` : ''}
                                        <span class="answer-text">${this.escapeHtml(answer.text)}</span>
                                    </span>
                                </label>
                            `).join('')}
                        </div>
                    </div>
                    <div class="poll-footer">
                        <button class="poll-submit-btn" type="button">Vote</button>
                    </div>
                    <div class="poll-results-container" style="display: none;"></div>
                </div>
            `;
            
            this.container.html(html);
            this.attachEvents();
        }

        renderStepByStepPoll() {
            const answersPerStep = this.poll.settings.answers_per_step || 2;
            this.totalSteps = Math.ceil(this.poll.answers.length / answersPerStep);
            
            // Initialize current step if not set
            if (this.currentStep >= this.totalSteps) {
                this.currentStep = 0;
            }
            
            const startIndex = this.currentStep * answersPerStep;
            const endIndex = Math.min(startIndex + answersPerStep, this.poll.answers.length);
            const currentAnswers = this.poll.answers.slice(startIndex, endIndex);
            
            const inputType = this.poll.settings.allow_multiple ? 'checkbox' : 'radio';
            const needsUserInfo = this.poll.settings.collect_name || this.poll.settings.collect_email;
            
            const html = `
                <div class="poll-wrapper step-by-step">
                    ${this.poll.featured_image ? `
                        <div class="poll-featured-image">
                            <img src="${this.poll.featured_image}" alt="${this.escapeHtml(this.poll.poll_name)}">
                        </div>
                    ` : ''}
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                        ${this.poll.description ? `<p class="poll-description">${this.escapeHtml(this.poll.description)}</p>` : ''}
                    </div>
                    
                    ${this.renderStepNavigation()}
                    
                    ${needsUserInfo && this.currentStep === 0 ? this.renderUserInfoFields() : ''}
                    
                    <div class="poll-content">
                        <div class="poll-step-content">
                            <div class="step-header">
                                <h3>${this.getStepTitle()}</h3>
                                <p class="step-description">Choose your answer${this.poll.settings.allow_multiple ? 's' : ''} for this step</p>
                            </div>
                            <div class="poll-answers">
                                ${currentAnswers.map((answer) => `
                                    <label class="poll-answer-option">
                                        <input type="${inputType}" name="poll_answer" value="${answer.id}" 
                                               ${this.selectedAnswers.includes(answer.id) ? 'checked' : ''}>
                                        <span class="answer-content">
                                            ${answer.image_url ? `<img src="${answer.image_url}" alt="Answer image" class="answer-image">` : ''}
                                            <span class="answer-text">${this.escapeHtml(answer.text)}</span>
                                        </span>
                                    </label>
                                `).join('')}
                            </div>
                        </div>
                    </div>
                    
                    <div class="poll-footer step-footer">
                        <div class="step-buttons">
                            ${this.currentStep > 0 ? '<button class="poll-prev-btn" type="button">Previous</button>' : ''}
                            ${this.currentStep < this.totalSteps - 1 ? 
                                '<button class="poll-next-btn" type="button">Next</button>' : 
                                '<button class="poll-submit-btn" type="button">Submit Vote</button>'
                            }
                        </div>
                    </div>
                    
                    <div class="poll-results-container" style="display: none;"></div>
                </div>
            `;
            
            this.container.html(html);
            this.attachStepEvents();
            
            // Restore user form data after rendering
            this.restoreUserFormData();
        }

        renderUserInfoFields() {
            const collectName = this.poll.settings.collect_name;
            const collectEmail = this.poll.settings.collect_email;
            
            if (!collectName && !collectEmail) {
                return '';
            }
            
            return `
                <div class="poll-user-info">
                    <h3>Your Information</h3>
                    <div class="user-info-fields">
                        ${collectName ? `
                            <div class="form-field">
                                <label for="poll-user-name">Name *</label>
                                <input type="text" id="poll-user-name" name="user_name" placeholder="Enter your name" required>
                            </div>
                        ` : ''}
                        ${collectEmail ? `
                            <div class="form-field">
                                <label for="poll-user-email">Email *</label>
                                <input type="email" id="poll-user-email" name="user_email" placeholder="Enter your email" required>
                            </div>
                        ` : ''}
                    </div>
                </div>
            `;
        }

        renderStepNavigation() {
            const stepNavigation = this.poll.settings.step_navigation || 'numbers';
            
            if (stepNavigation === 'progress_bar') {
                const progress = ((this.currentStep + 1) / this.totalSteps) * 100;
                return `
                    <div class="step-navigation progress-bar">
                        <div class="progress-container">
                            <div class="progress-bar-fill" style="width: ${progress}%"></div>
                        </div>
                        <div class="progress-text">Step ${this.currentStep + 1} of ${this.totalSteps}</div>
                    </div>
                `;
            } else if (stepNavigation === 'custom' && this.poll.settings.custom_step_labels) {
                const labels = this.poll.settings.custom_step_labels;
                return `
                    <div class="step-navigation custom-labels">
                        <div class="step-indicators">
                            ${Array.from({length: this.totalSteps}, (_, i) => `
                                <div class="step-indicator ${i === this.currentStep ? 'active' : ''} ${i < this.currentStep ? 'completed' : ''}">
                                    <div class="step-circle">${i + 1}</div>
                                    <div class="step-label">${labels[i] || `Step ${i + 1}`}</div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `;
            } else {
                // Default numbers
                return `
                    <div class="step-navigation numbers">
                        <div class="step-indicators">
                            ${Array.from({length: this.totalSteps}, (_, i) => `
                                <div class="step-indicator ${i === this.currentStep ? 'active' : ''} ${i < this.currentStep ? 'completed' : ''}">
                                    <div class="step-circle">${i + 1}</div>
                                </div>
                            `).join('')}
                        </div>
                        <div class="step-text">Step ${this.currentStep + 1} of ${this.totalSteps}</div>
                    </div>
                `;
            }
        }

        getStepTitle() {
            const stepNavigation = this.poll.settings.step_navigation || 'numbers';
            
            if (stepNavigation === 'custom' && this.poll.settings.custom_step_labels) {
                const labels = this.poll.settings.custom_step_labels;
                return labels[this.currentStep] || `Step ${this.currentStep + 1}`;
            }
            
            return `Step ${this.currentStep + 1}`;
        }

        attachStepEvents() {
            const self = this;
            
            // Previous button
            this.container.find('.poll-prev-btn').on('click', function() {
                self.goToPreviousStep();
            });
            
            // Next button
            this.container.find('.poll-next-btn').on('click', function() {
                self.goToNextStep();
            });
            
            // Submit button
            this.container.find('.poll-submit-btn').on('click', function() {
                self.submitPoll();
            });
            
            // Save user form data when inputs change
            this.container.find('input[name="user_name"], input[name="user_email"]').on('input change', function() {
                self.saveUserFormData();
            });
            
            // Answer selection - handle both single question and multiple questions
            const pollType = self.poll.settings.poll_type || 'single_question';
            
            if (pollType === 'multiple_questions') {
                // Handle multiple questions step-by-step
                self.container.find('input[type="radio"], input[type="checkbox"]').on('change', function() {
                    // Update selected answers for current question
                    self.updateSelectedAnswersForMultipleQuestions();
                });
            } else {
                // Handle single question step-by-step
                self.container.find('input[name="poll_answer"]').on('change', function() {
                    self.updateSelectedAnswers();
                });
            }
        }

        goToPreviousStep() {
            const pollType = this.poll.settings.poll_type || 'single_question';
            
            // Save user form data before navigation
            this.saveUserFormData();
            
            if (pollType === 'multiple_questions') {
                this.updateSelectedAnswersForMultipleQuestions();
                if (this.currentStep > 0) {
                    this.currentStep--;
                    this.renderMultipleQuestionsStepByStep();
                    // Restore user form data after rendering
                    this.restoreUserFormData();
                }
            } else {
                // Original single question logic
                if (this.currentStep > 0) {
                    this.updateSelectedAnswers();
                    this.currentStep--;
                    this.renderStepByStepPoll();
                    // Restore user form data after rendering
                    this.restoreUserFormData();
                }
            }
        }

        updateSelectedAnswersForMultipleQuestions() {
            const questions = this.poll.questions || [];
            const currentQuestion = questions[this.currentStep];
            
            if (!currentQuestion) return;
            
            // Get current step answers
            const currentStepAnswers = [];
            this.container.find(`input[name="question_${currentQuestion.id}"]:checked`).each(function() {
                currentStepAnswers.push($(this).val());
            });
            
            // Remove previous answers for this question
            this.selectedAnswers = this.selectedAnswers.filter(answerId => {
                return !currentQuestion.answers.some(answer => answer.id === answerId);
            });
            
            // Add current step answers
            this.selectedAnswers = this.selectedAnswers.concat(currentStepAnswers);
        }

        saveUserFormData() {
            // Save user information if present
            const userName = this.container.find('input[name="user_name"]').val();
            const userEmail = this.container.find('input[name="user_email"]').val();
            
            if (userName !== undefined) {
                this.userFormData.user_name = userName;
            }
            if (userEmail !== undefined) {
                this.userFormData.user_email = userEmail;
            }
        }

        restoreUserFormData() {
            // Restore user information if available
            if (this.userFormData.user_name !== undefined) {
                this.container.find('input[name="user_name"]').val(this.userFormData.user_name);
            }
            if (this.userFormData.user_email !== undefined) {
                this.container.find('input[name="user_email"]').val(this.userFormData.user_email);
            }
        }

        goToNextStep() {
            const pollType = this.poll.settings.poll_type || 'single_question';
            
            // Save user form data before navigation
            this.saveUserFormData();
            
            if (pollType === 'multiple_questions') {
                this.updateSelectedAnswersForMultipleQuestions();
                
                // Validate current question if required
                const questions = this.poll.questions || [];
                const currentQuestion = questions[this.currentStep];
                
                if (currentQuestion && currentQuestion.required) {
                    const currentStepAnswers = this.container.find(`input[name="question_${currentQuestion.id}"]:checked`);
                    if (currentStepAnswers.length === 0) {
                        showCustomModal('Required Question', 'Please answer this question before proceeding.', 3000, 'error');
                        return;
                    }
                }
                
                if (this.currentStep < this.totalSteps - 1) {
                    this.currentStep++;
                    this.renderMultipleQuestionsStepByStep();
                    // Restore user form data after rendering
                    this.restoreUserFormData();
                }
            } else {
                // Original single question logic
                this.updateSelectedAnswers();
                
                // Validate current step has selection (if not multiple choice)
                const currentStepAnswers = this.container.find('input[name="poll_answer"]:checked');
                if (currentStepAnswers.length === 0 && !this.poll.settings.allow_multiple) {
                    showCustomModal('Please Select', 'Please select an answer before proceeding to the next step.', 3000, 'error');
                    return;
                }
                
                if (this.currentStep < this.totalSteps - 1) {
                    this.currentStep++;
                    this.renderStepByStepPoll();
                    // Restore user form data after rendering
                    this.restoreUserFormData();
                }
            }
        }

        updateSelectedAnswers() {
            const checkedAnswers = [];
            this.container.find('input[name="poll_answer"]:checked').each(function() {
                checkedAnswers.push($(this).val());
            });
            
            // For step-by-step, we need to merge with previously selected answers
            if (this.poll.settings.display_mode === 'step_by_step') {
                // Remove answers from current step first
                const answersPerStep = this.poll.settings.answers_per_step || 2;
                const startIndex = this.currentStep * answersPerStep;
                const endIndex = Math.min(startIndex + answersPerStep, this.poll.answers.length);
                const currentStepAnswerIds = this.poll.answers.slice(startIndex, endIndex).map(a => a.id);
                
                // Remove current step answers from selectedAnswers
                this.selectedAnswers = this.selectedAnswers.filter(id => !currentStepAnswerIds.includes(id));
                
                // Add newly checked answers
                this.selectedAnswers = this.selectedAnswers.concat(checkedAnswers);
            } else {
                this.selectedAnswers = checkedAnswers;
            }
        }

        attachEvents() {
            const self = this;
            
            this.container.find('.poll-submit-btn').on('click', function() {
                self.submitPoll();
            });
            
            // Save user form data when inputs change
            this.container.find('input[name="user_name"], input[name="user_email"]').on('input change', function() {
                self.saveUserFormData();
            });
            
            // Add event handlers for multiple questions
            const pollType = this.poll.settings.poll_type || 'single_question';
            if (pollType === 'multiple_questions') {
                // Handle answer selection for multiple questions
                this.container.find('input[type="radio"], input[type="checkbox"]').on('change', function() {
                    // Optional: Add visual feedback or validation
                });
            }
        }

        submitPoll() {
            const self = this;
            
            // Save current user form data before submission
            this.saveUserFormData();
            
            // Collect user info if needed
            if (this.poll.settings.collect_name || this.poll.settings.collect_email) {
                // Use saved form data instead of reading from DOM
                const userName = this.userFormData.user_name || '';
                const userEmail = this.userFormData.user_email || '';
                
                // Validate user info
                if (this.poll.settings.collect_name && !userName) {
                    showCustomModal('Error', 'Please enter your name.', 3000, 'error');
                    // Try to focus the field if it's visible
                    const nameField = this.container.find('input[name="user_name"]');
                    if (nameField.length > 0) {
                        nameField.focus();
                    }
                    return;
                }
                
                if (this.poll.settings.collect_email && !userEmail) {
                    showCustomModal('Error', 'Please enter your email.', 3000, 'error');
                    // Try to focus the field if it's visible
                    const emailField = this.container.find('input[name="user_email"]');
                    if (emailField.length > 0) {
                        emailField.focus();
                    }
                    return;
                }
                
                // Validate email format
                if (this.poll.settings.collect_email && userEmail) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(userEmail)) {
                        showCustomModal('Error', 'Please enter a valid email address.', 3000, 'error');
                        // Try to focus the field if it's visible
                        const emailField = this.container.find('input[name="user_email"]');
                        if (emailField.length > 0) {
                            emailField.focus();
                        }
                        return;
                    }
                }
                
                this.userData = {
                    user_name: userName,
                    user_email: userEmail
                };
            }
            
            // Collect selected answers
            let selectedAnswers = [];
            
            const pollType = this.poll.settings.poll_type || 'single_question';
            
            if (pollType === 'multiple_questions') {
                // Handle multiple questions survey
                const displayMode = this.poll.settings.display_mode || 'all_at_once';
                
                if (displayMode === 'step_by_step') {
                    // Update current step selections first
                    this.updateSelectedAnswersForMultipleQuestions();
                    selectedAnswers = this.selectedAnswers;
                    
                    // Validate all required questions have been answered
                    const questions = this.poll.questions || [];
                    const answeredQuestions = new Set();
                    
                    // Group selected answers by question
                    questions.forEach(question => {
                        const hasAnswers = question.answers.some(answer => 
                            selectedAnswers.includes(answer.id)
                        );
                        if (hasAnswers) {
                            answeredQuestions.add(question.id);
                        }
                    });
                    
                    // Check if all required questions are answered
                    const unansweredRequired = questions.filter(q => 
                        q.required && !answeredQuestions.has(q.id)
                    );
                    
                    if (unansweredRequired.length > 0) {
                        showCustomModal('Error', 'Please answer all required questions before submitting.', 3000, 'error');
                        return;
                    }
                } else {
                    // All at once - collect all answers from all questions
                    const questions = this.poll.questions || [];
                    const surveyAnswers = {};
                    
                    questions.forEach(question => {
                        const questionAnswers = [];
                        this.container.find(`input[name="question_${question.id}"]:checked`).each(function() {
                            questionAnswers.push($(this).val());
                        });
                        
                        if (questionAnswers.length > 0) {
                            surveyAnswers[question.id] = questionAnswers;
                        }
                        
                        // Validate required questions
                        if (question.required && questionAnswers.length === 0) {
                            showCustomModal('Error', `Please answer the required question: "${question.text}"`, 3000, 'error');
                            return;
                        }
                    });
                    
                    // Convert to flat array for backend compatibility
                    selectedAnswers = Object.values(surveyAnswers).flat();
                }
            } else if (this.poll.settings.display_mode === 'step_by_step') {
                // Single question step-by-step
                this.updateSelectedAnswers();
                selectedAnswers = this.selectedAnswers;
            } else {
                // Traditional single question all at once
                this.container.find('input[name="poll_answer"]:checked').each(function() {
                    selectedAnswers.push($(this).val());
                });
            }

            // Validate
            if (selectedAnswers.length === 0) {
                showCustomModal('Error', 'Please select at least one answer.', 3000, 'error');
                return;
            }

            // Show processing modal
            showCustomModal('Processing', 'Submitting your vote...', 30000, 'info');

            // Submit
            $.ajax({
                url: simpleform_ajax.ajax_url,
                type: 'POST',
                data: {
                    action: 'simpleform_submit_poll',
                    poll_id: self.pollId,
                    selected_answers: selectedAnswers,
                    user_data: self.userData,
                    nonce: simpleform_ajax.nonce
                },
                success: function(response) {
                    // Close processing modal
                    $('.custom-modal-overlay').remove();
                    
                    if (response.success) {
                        // Set local vote status based on tracking method
                        const voteTracking = self.poll.settings.vote_tracking || 'hybrid';
                        
                        if (voteTracking === 'browser_storage' || voteTracking === 'hybrid') {
                            self.setLocalVoteStatus({
                                selectedAnswers: selectedAnswers,
                                userData: self.userData
                            });
                        }
                        
                        showCustomModal('Success', 'Vote submitted successfully!', 2000, 'success');
                        
                        setTimeout(() => {
                            self.handlePostSubmissionBehavior(response);
                        }, 2000);
                    } else {
                        showCustomModal('Error', response.data.message || 'Failed to submit vote', 3000, 'error');
                    }
                },
                error: function() {
                    // Close processing modal
                    $('.custom-modal-overlay').remove();
                    showCustomModal('Error', 'Failed to submit vote. Please try again.', 3000, 'error');
                }
            });
        }

        handlePostSubmissionBehavior(response) {
            const settings = this.poll.settings;
            
            // Handle reward type first
            if (settings.reward_type === 'redirect' && settings.redirect_url) {
                window.location.href = settings.redirect_url;
                return;
            }
            
            if (settings.reward_type === 'coupon' && settings.coupon_code) {
                this.showCouponReward(settings.coupon_code);
                return;
            }
            
            // Handle poll visibility after submission based on "Hide poll after submission" setting
            if (settings.hide_after_submit) {
                // Hide the poll and show results or completion message
                if (response.data.results && settings.show_results !== 'never') {
                    this.renderResults(response.data.results);
                } else {
                    this.showCompletedMessage();
                }
            } else {
                // Don't hide poll, but show appropriate content based on allow_revote setting
                if (settings.allow_revote) {
                    // Keep poll visible and show results if enabled
                    if (response.data.results && settings.show_results !== 'never') {
                        this.renderResults(response.data.results);
                    } else {
                        // Just show a success message but keep poll visible
                        this.showSuccessMessage(response.data.message || 'Thank you for voting!');
                    }
                } else {
                    // Show thank you card but don't hide the poll container
                    this.showCompletedMessage();
                }
            }
        }

        showCouponReward(couponCode) {
            const html = `
                <div class="poll-reward">
                    <div class="poll-header">
                        <h2 class="poll-title">${this.escapeHtml(this.poll.poll_name)}</h2>
                    </div>
                    <div class="reward-content">
                        <h3>🎉 Thank you for voting!</h3>
                        <p>Here's your reward:</p>
                        <div class="coupon-code" data-coupon="${this.escapeHtml(couponCode)}">
                            <strong>${this.escapeHtml(couponCode)}</strong>
                        </div>
                        <p><small>Click the coupon code above to copy it</small></p>
                    </div>
                </div>
            `;
            
            this.container.html(html);
            
            // Add click event for copying coupon code
            this.container.find('.coupon-code').on('click', function() {
                const couponElement = $(this);
                const couponText = couponElement.data('coupon');
                
                // Copy to clipboard
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(couponText).then(() => {
                        // Show copied state
                        couponElement.addClass('copied');
                        showCustomModal('Copied!', `Coupon code "${couponText}" copied to clipboard`, 2000, 'success');
                        
                        // Remove copied state after 3 seconds
                        setTimeout(() => {
                            couponElement.removeClass('copied');
                        }, 3000);
                    }).catch(() => {
                        // Fallback for older browsers
                        fallbackCopyTextToClipboard(couponText, couponElement);
                    });
                } else {
                    // Fallback for older browsers
                    fallbackCopyTextToClipboard(couponText, couponElement);
                }
            });
            
            // Fallback copy function for older browsers
            function fallbackCopyTextToClipboard(text, element) {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    const successful = document.execCommand('copy');
                    if (successful) {
                        element.addClass('copied');
                        showCustomModal('Copied!', `Coupon code "${text}" copied to clipboard`, 2000, 'success');
                        
                        setTimeout(() => {
                            element.removeClass('copied');
                        }, 3000);
                    } else {
                        showCustomModal('Error', 'Failed to copy coupon code', 2000, 'error');
                    }
                } catch (err) {
                    showCustomModal('Error', 'Failed to copy coupon code', 2000, 'error');
                }
                
                document.body.removeChild(textArea);
            }
        }

        renderResults(results) {
            const html = `
                <div class="poll-results-content">
                    <h3>Poll Results</h3>
                    <p class="total-votes">Total Votes: ${results.total_votes}</p>
                    <div class="results-list">
                        ${results.answers.map(answer => `
                            <div class="result-item">
                                <div class="result-header">
                                    <span class="result-text">${this.escapeHtml(answer.text)}</span>
                                    <span class="result-stats">
                                        ${this.poll.settings.result_display === 'percentage' || this.poll.settings.result_display === 'both' ? 
                                            `<span class="percentage">${answer.percentage}%</span>` : ''}
                                        ${this.poll.settings.result_display === 'count' || this.poll.settings.result_display === 'both' ? 
                                            `<span class="count">${answer.votes} votes</span>` : ''}
                                    </span>
                                </div>
                                <div class="result-bar">
                                    <div class="result-bar-fill" style="width: ${answer.percentage}%"></div>
                                </div>
                            </div>
                        `).join('')}
                    </div>
                </div>
            `;
            
            // Only hide poll content if "Hide poll after submission" is enabled
            if (this.poll.settings.hide_after_submit) {
                this.container.find('.poll-content, .poll-footer, .poll-user-info').hide();
                this.container.find('.poll-results-container').html(html).show();
            } else {
                // Show results but keep poll visible (maybe in a separate section)
                this.container.find('.poll-results-container').html(html).show();
            }
        }

        showMessage(message) {
            this.container.html(`<div class="poll-message">${this.escapeHtml(message)}</div>`);
        }

        showSuccessMessage(message) {
            // Show a temporary success message without replacing the poll
            showCustomModal('Success', message, 3000, 'success');
            
            // Reload the poll to show updated state
            setTimeout(() => {
                this.loadPoll();
            }, 3000);
        }

        showError(message) {
            this.container.html(`<div class="poll-error">${this.escapeHtml(message)}</div>`);
        }

        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }
    }

    // Initialize polls on page load
    $(document).ready(function() {
        $('.simpleform-poll-container').each(function() {
            new PollFrontend(this);
        });
    });

})(jQuery);